import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Globe,
  Phone,
  Mail,
  MapPin,
  Clock,
  Image,
  Palette,
  Facebook,
  Instagram,
  Youtube,
  Save,
  Loader2,
  Eye,
  Upload,
  ExternalLink,
  Settings2,
  Type,
  ToggleLeft,
  FileText,
  BarChart3,
  Code,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import CMSPagesManager from '@/components/CMSPagesManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WebsiteSettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [savedSettings, setSavedSettings] = useState(null); // Valeurs réellement en DB
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoInputRef = useRef(null);
  const heroInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/cms/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setSavedSettings(data); // Snapshot DB
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/cms/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      if (response.ok) {
        toast.success('Paramètres enregistrés avec succès');
        setSavedSettings({ ...settings }); // Mettre à jour le snapshot DB
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (file, field) => {
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, [field]: data.url }));
        toast.success('Image téléchargée');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="p-6">
        <p className="text-red-500">Erreur lors du chargement des paramètres</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            <Globe className="w-6 h-6 inline mr-2 text-yellow-500" />
            Gestion du Site Web
          </h1>
          <p className="text-slate-500 mt-1">Personnalisez le contenu de votre portail client</p>
        </div>
        <div className="flex gap-3">
          <a
            href="/portail"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition"
          >
            <Eye className="w-4 h-4" />
            Voir le site
            <ExternalLink className="w-3 h-3" />
          </a>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-yellow-500 hover:bg-yellow-600 text-white"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="general" className="data-[state=active]:bg-white">
            <Settings2 className="w-4 h-4 mr-2" />
            Général
          </TabsTrigger>
          <TabsTrigger value="contact" className="data-[state=active]:bg-white">
            <Phone className="w-4 h-4 mr-2" />
            Contact
          </TabsTrigger>
          <TabsTrigger value="images" className="data-[state=active]:bg-white">
            <Image className="w-4 h-4 mr-2" />
            Images
          </TabsTrigger>
          <TabsTrigger value="texts" className="data-[state=active]:bg-white">
            <Type className="w-4 h-4 mr-2" />
            Textes
          </TabsTrigger>
          <TabsTrigger value="display" className="data-[state=active]:bg-white">
            <ToggleLeft className="w-4 h-4 mr-2" />
            Affichage
          </TabsTrigger>
          <TabsTrigger value="tracking" className="data-[state=active]:bg-white">
            <BarChart3 className="w-4 h-4 mr-2" />
            Tracking
          </TabsTrigger>
          <TabsTrigger value="pages" className="data-[state=active]:bg-white">
            <FileText className="w-4 h-4 mr-2" />
            Pages
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Identité du site</CardTitle>
                <CardDescription>Nom et slogan de votre entreprise</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="site_name">Nom du site</Label>
                  <Input
                    id="site_name"
                    value={settings.site_name || ''}
                    onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                    placeholder="IDMAT OI"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="site_tagline">Slogan</Label>
                  <Input
                    id="site_tagline"
                    value={settings.site_tagline || ''}
                    onChange={(e) => setSettings({ ...settings, site_tagline: e.target.value })}
                    placeholder="Mini-Pelles Professionnelles"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta_description">Description SEO</Label>
                  <Textarea
                    id="meta_description"
                    value={settings.meta_description || ''}
                    onChange={(e) => setSettings({ ...settings, meta_description: e.target.value })}
                    placeholder="Description pour les moteurs de recherche..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Couleurs
                </CardTitle>
                <CardDescription>Personnalisez l'apparence du site</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="primary_color">Couleur principale</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      id="primary_color"
                      value={settings.primary_color || '#f59e0b'}
                      onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                      className="w-12 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={settings.primary_color || '#f59e0b'}
                      onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondary_color">Couleur secondaire</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      id="secondary_color"
                      value={settings.secondary_color || '#1e293b'}
                      onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                      className="w-12 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={settings.secondary_color || '#1e293b'}
                      onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: settings.secondary_color }}>
                  <p className="text-white mb-2">Aperçu des couleurs</p>
                  <button 
                    className="px-4 py-2 rounded font-medium"
                    style={{ backgroundColor: settings.primary_color }}
                  >
                    Bouton exemple
                  </button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Réseaux sociaux</CardTitle>
                <CardDescription>Liens vers vos réseaux</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="facebook_url" className="flex items-center gap-2">
                    <Facebook className="w-4 h-4 text-blue-600" />
                    Facebook
                  </Label>
                  <Input
                    id="facebook_url"
                    value={settings.facebook_url || ''}
                    onChange={(e) => setSettings({ ...settings, facebook_url: e.target.value })}
                    placeholder="https://facebook.com/votrepage"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="instagram_url" className="flex items-center gap-2">
                    <Instagram className="w-4 h-4 text-pink-600" />
                    Instagram
                  </Label>
                  <Input
                    id="instagram_url"
                    value={settings.instagram_url || ''}
                    onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })}
                    placeholder="https://instagram.com/votrecompte"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="youtube_url" className="flex items-center gap-2">
                    <Youtube className="w-4 h-4 text-red-600" />
                    YouTube
                  </Label>
                  <Input
                    id="youtube_url"
                    value={settings.youtube_url || ''}
                    onChange={(e) => setSettings({ ...settings, youtube_url: e.target.value })}
                    placeholder="https://youtube.com/votrechaine"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Contact Tab */}
        <TabsContent value="contact">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Coordonnées</CardTitle>
              <CardDescription>Informations de contact affichées sur le site</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="contact_phone" className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-green-600" />
                    Téléphone
                  </Label>
                  <Input
                    id="contact_phone"
                    value={settings.contact_phone || ''}
                    onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                    placeholder="0693 52 64 49"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-blue-600" />
                    Email
                  </Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={settings.contact_email || ''}
                    onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                    placeholder="contact@idmatoi.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_address" className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-red-600" />
                    Adresse
                  </Label>
                  <Textarea
                    id="contact_address"
                    value={settings.contact_address || ''}
                    onChange={(e) => setSettings({ ...settings, contact_address: e.target.value })}
                    placeholder="Votre adresse complète"
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_hours" className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-orange-600" />
                    Horaires d'ouverture
                  </Label>
                  <Textarea
                    id="contact_hours"
                    value={settings.contact_hours || ''}
                    onChange={(e) => setSettings({ ...settings, contact_hours: e.target.value })}
                    placeholder="Lundi - Vendredi : 8h - 17h"
                    rows={2}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Images Tab */}
        <TabsContent value="images">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Logo</CardTitle>
                <CardDescription>Logo affiché dans la navigation</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {settings.logo_url && (
                  <div className="p-4 bg-slate-100 rounded-lg">
                    <img
                      src={settings.logo_url.startsWith('/api/') ? `${API_URL}${settings.logo_url}` : settings.logo_url}
                      alt="Logo"
                      className="h-16 object-contain"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>URL du logo</Label>
                  <Input
                    value={settings.logo_url || ''}
                    onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
                    placeholder="https://... ou /api/uploads/..."
                  />
                </div>
                <input
                  type="file"
                  ref={logoInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e.target.files[0], 'logo_url')}
                />
                <Button
                  variant="outline"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Télécharger un logo
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Image Hero</CardTitle>
                <CardDescription>Image principale de la page d'accueil</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {settings.hero_image_url && (
                  <div className="p-4 bg-slate-100 rounded-lg">
                    <img
                      src={settings.hero_image_url.startsWith('/api/') ? `${API_URL}${settings.hero_image_url}` : settings.hero_image_url}
                      alt="Hero"
                      className="w-full h-32 object-cover rounded"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>URL de l'image</Label>
                  <Input
                    value={settings.hero_image_url || ''}
                    onChange={(e) => setSettings({ ...settings, hero_image_url: e.target.value })}
                    placeholder="https://... ou /api/uploads/..."
                  />
                </div>
                <input
                  type="file"
                  ref={heroInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e.target.files[0], 'hero_image_url')}
                />
                <Button
                  variant="outline"
                  onClick={() => heroInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Télécharger une image
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Texts Tab */}
        <TabsContent value="texts">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Textes du site</CardTitle>
              <CardDescription>Personnalisez les textes affichés sur le portail</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="hero_title">Titre principal (Hero)</Label>
                  <Input
                    id="hero_title"
                    value={settings.hero_title || ''}
                    onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
                    placeholder="Votre partenaire mini-pelles à La Réunion"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hero_subtitle">Sous-titre (Hero)</Label>
                  <Input
                    id="hero_subtitle"
                    value={settings.hero_subtitle || ''}
                    onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
                    placeholder="Vente et location de mini-pelles professionnelles"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cta_text">Texte du bouton principal</Label>
                <Input
                  id="cta_text"
                  value={settings.cta_text || ''}
                  onChange={(e) => setSettings({ ...settings, cta_text: e.target.value })}
                  placeholder="Demander un devis"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="about_text">Texte "À propos"</Label>
                <Textarea
                  id="about_text"
                  value={settings.about_text || ''}
                  onChange={(e) => setSettings({ ...settings, about_text: e.target.value })}
                  placeholder="Présentez votre entreprise en quelques lignes..."
                  rows={5}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Display Tab */}
        <TabsContent value="display">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Options d'affichage</CardTitle>
              <CardDescription>Activez ou désactivez les sections du site</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-base">Afficher les prix</Label>
                    <p className="text-sm text-slate-500">Montrer les prix des machines sur le site public</p>
                  </div>
                  <Switch
                    checked={settings.show_prices || false}
                    onCheckedChange={(checked) => setSettings({ ...settings, show_prices: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-base">Afficher les témoignages</Label>
                    <p className="text-sm text-slate-500">Section témoignages clients sur la page d'accueil</p>
                  </div>
                  <Switch
                    checked={settings.show_testimonials !== false}
                    onCheckedChange={(checked) => setSettings({ ...settings, show_testimonials: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-base">Afficher les statistiques</Label>
                    <p className="text-sm text-slate-500">Chiffres clés (clients satisfaits, machines vendues...)</p>
                  </div>
                  <Switch
                    checked={settings.show_stats !== false}
                    onCheckedChange={(checked) => setSettings({ ...settings, show_stats: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-base">Afficher les partenaires</Label>
                    <p className="text-sm text-slate-500">Logos des partenaires et marques</p>
                  </div>
                  <Switch
                    checked={settings.show_partners !== false}
                    onCheckedChange={(checked) => setSettings({ ...settings, show_partners: checked })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pages Tab */}
        <TabsContent value="pages">
          <CMSPagesManager />
        </TabsContent>

        {/* Tracking Tab */}
        <TabsContent value="tracking">
          <div className="space-y-6">
            {/* === PANNEAU DE STATUT === */}
            <Card className="border-0 bg-slate-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-slate-600" />
                  Statut des balises de tracking
                </CardTitle>
                <CardDescription>État en temps réel des balises configurées et sauvegardées</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {[
                    { key: 'google_tag_manager_id', label: 'GTM', icon: '🏷️' },
                    { key: 'google_analytics_id', label: 'Analytics', icon: '📊' },
                    { key: 'google_ads_id', label: 'Google Ads', icon: '🎯' },
                    { key: 'facebook_pixel_id', label: 'Facebook Pixel', icon: '📘' },
                    { key: 'google_ads_conversion_label', label: 'Conversion Label', icon: '🔄' },
                  ].map(({ key, label, icon }) => {
                    const savedVal = savedSettings?.[key];
                    const currentVal = settings?.[key];
                    const isActive = !!savedVal;
                    const isModified = currentVal !== savedVal;
                    const hasValue = !!currentVal;

                    let statusColor, statusText, StatusIcon;
                    if (isActive && !isModified) {
                      statusColor = 'bg-green-50 border-green-200 text-green-700';
                      statusText = 'Actif';
                      StatusIcon = <CheckCircle2 className="w-4 h-4 text-green-500" />;
                    } else if (hasValue && isModified) {
                      statusColor = 'bg-amber-50 border-amber-200 text-amber-700';
                      statusText = 'Non sauvegardé';
                      StatusIcon = <AlertCircle className="w-4 h-4 text-amber-500" />;
                    } else {
                      statusColor = 'bg-slate-100 border-slate-200 text-slate-500';
                      statusText = 'Inactif';
                      StatusIcon = <XCircle className="w-4 h-4 text-slate-400" />;
                    }

                    return (
                      <div key={key} className={`flex flex-col gap-1.5 p-3 rounded-xl border ${statusColor}`} data-testid={`tracking-status-${key}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-base">{icon}</span>
                          {StatusIcon}
                        </div>
                        <p className="text-xs font-semibold">{label}</p>
                        <p className="text-xs opacity-75">{statusText}</p>
                        {savedVal && (
                          <p className="text-xs font-mono opacity-60 truncate" title={savedVal}>{savedVal}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
                {savedSettings && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                    <RefreshCw className="w-3 h-3" />
                    Les balises actives sont injectées automatiquement sur le site public.
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Google Tags */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Google
                  </CardTitle>
                  <CardDescription>Intégrations Google Analytics, Tag Manager et Ads</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { id: 'gtm_id', field: 'google_tag_manager_id', label: 'Google Tag Manager ID', placeholder: 'GTM-XXXXXXX', hint: 'Le conteneur qui gère tous vos tags' },
                    { id: 'ga_id', field: 'google_analytics_id', label: 'Google Analytics ID', placeholder: 'G-XXXXXXXXXX ou UA-XXXXXXXX-X', hint: 'Pour le suivi des visiteurs' },
                    { id: 'gads_id', field: 'google_ads_id', label: 'Google Ads ID', placeholder: 'AW-XXXXXXXXX', hint: 'Pour le remarketing et les conversions' },
                    { id: 'gads_conversion', field: 'google_ads_conversion_label', label: 'Label de conversion Google Ads', placeholder: 'XXXXXXXXXXXX', hint: 'Pour le suivi des conversions (demandes de devis)' },
                  ].map(({ id, field, label, placeholder, hint }) => {
                    const savedVal = savedSettings?.[field];
                    const currentVal = settings?.[field];
                    const isActive = !!savedVal && currentVal === savedVal;
                    const isModified = !!currentVal && currentVal !== savedVal;
                    return (
                      <div key={id} className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <Label htmlFor={id}>{label}</Label>
                          {isActive && <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium"><CheckCircle2 className="w-3 h-3" /> Actif</span>}
                          {isModified && <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium"><AlertCircle className="w-3 h-3" /> Non sauvegardé</span>}
                        </div>
                        <Input
                          id={id}
                          value={currentVal || ''}
                          onChange={(e) => setSettings({ ...settings, [field]: e.target.value || null })}
                          placeholder={placeholder}
                          className={isActive ? 'border-green-300 focus-visible:ring-green-400' : isModified ? 'border-amber-300 focus-visible:ring-amber-400' : ''}
                        />
                        <p className="text-xs text-slate-500">{hint}</p>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Facebook Pixel */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Facebook className="w-5 h-5 text-blue-600" />
                    Facebook / Meta
                  </CardTitle>
                  <CardDescription>Pixel Facebook pour le remarketing</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(() => {
                    const savedVal = savedSettings?.facebook_pixel_id;
                    const currentVal = settings?.facebook_pixel_id;
                    const isActive = !!savedVal && currentVal === savedVal;
                    const isModified = !!currentVal && currentVal !== savedVal;
                    return (
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="fb_pixel">Facebook Pixel ID</Label>
                          {isActive && <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium"><CheckCircle2 className="w-3 h-3" /> Actif</span>}
                          {isModified && <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium"><AlertCircle className="w-3 h-3" /> Non sauvegardé</span>}
                        </div>
                        <Input
                          id="fb_pixel"
                          value={currentVal || ''}
                          onChange={(e) => setSettings({ ...settings, facebook_pixel_id: e.target.value || null })}
                          placeholder="XXXXXXXXXXXXXXXX"
                          className={isActive ? 'border-green-300 focus-visible:ring-green-400' : isModified ? 'border-amber-300 focus-visible:ring-amber-400' : ''}
                        />
                        <p className="text-xs text-slate-500">Pour le suivi des conversions et le remarketing Facebook/Instagram</p>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Custom Scripts */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Code className="w-5 h-5 text-slate-600" />
                    Scripts personnalisés
                  </CardTitle>
                  <CardDescription>Ajoutez vos propres scripts de tracking ou widgets</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="head_scripts">Scripts dans &lt;head&gt;</Label>
                    <Textarea
                      id="head_scripts"
                      value={settings.custom_head_scripts || ''}
                      onChange={(e) => setSettings({ ...settings, custom_head_scripts: e.target.value })}
                      placeholder="<!-- Collez ici vos scripts pour <head> -->"
                      rows={5}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-slate-500">Ces scripts seront insérés dans la balise &lt;head&gt; de chaque page</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="body_scripts">Scripts dans &lt;body&gt;</Label>
                    <Textarea
                      id="body_scripts"
                      value={settings.custom_body_scripts || ''}
                      onChange={(e) => setSettings({ ...settings, custom_body_scripts: e.target.value })}
                      placeholder="<!-- Collez ici vos scripts pour <body> -->"
                      rows={5}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-slate-500">Ces scripts seront insérés juste avant la fermeture de &lt;/body&gt;</p>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                    <h4 className="font-medium text-yellow-800 mb-2">Conseil</h4>
                    <p className="text-sm text-yellow-700">
                      Si vous utilisez Google Tag Manager, vous n'avez besoin que de l'ID GTM. 
                      Configurez ensuite tous vos autres tags (Analytics, Ads, Facebook) directement dans GTM.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
